/**
 * 四档可见受众 —— 闭集常量 + 三层解析 + 展示合成口径（前后端共用）
 *
 * 契约源：dev-docs `03-数据字典/20-内容.md` §6（四档语义）/ §6.0（三态→四档迁移映射）/
 * §1.2（三层解析）；`02-API契约/A6-书籍域.md` §12（三层写）/ §15.1（列表展示合成口径）。
 *
 * 放 shared 而非 server：web 的列表标签 / 筛选器 / 写表单与 server 的查询、DTO 用的是同一套
 * 闭集与同一条合成口径，分开写必漂移（§6.1 明令「消费面一律引用具名规则，禁止内联判据串」）。
 */

import { z } from 'zod';

/**
 * 四档闭集（§6）。顺序即受众单调收窄：`PUBLIC ⊇ PAYING_ONLY ⊇ UNLOCKED_ONLY ⊇ ∅`。
 * DB 三张表的 CHECK 由 srv 侧同名常量生成，本常量是 admin 侧的唯一镜像。
 */
export const NOVEL_VISIBILITIES = ['PUBLIC', 'PAYING_ONLY', 'UNLOCKED_ONLY', 'DELISTED'] as const;

export type NovelVisibility = (typeof NOVEL_VISIBILITIES)[number];

export const NovelVisibilitySchema = z.enum(NOVEL_VISIBILITIES);

/**
 * 建组缺省档（§6.0「新建路径」）。
 *
 * **不是**存量迁移值 —— 那个是 `PUBLIC`，只出现在 srv migration 0187 的一次性回填里。
 * 两者分别承载「新建路径 fail closed」（未编辑完的新书不该进书城）与「保持存量行为不变」，
 * **不可互相推导**。admin 侧不建书组默认层的值（建组由 DB DEFAULT 落值），本常量仅供
 * 新建书组表单的初值回显与测试断言使用。
 */
export const GROUP_DEFAULT_VISIBILITY_ON_CREATE: NovelVisibility = 'UNLOCKED_ONLY';

/**
 * 三层解析（字典 §1.2 `effective_visibility(novel_id, app_id)`）：③ 单书×单App → ② 单书 → ① 书组默认。
 *
 * 形参按层次显式命名而非收窄成三个同型可空参数：三层同型，传反了 tsc 看不出来，
 * 而次序错误的后果是「运营以为下架了但某个 App 还在卖」。
 *
 * @param appOverride 层③ `novel_app_overrides.visibility`；无 active 覆盖行传 `null`
 * @param novelOverride 层② `novels.visibility`；`null` = 不覆盖、继承层①
 * @param groupDefault 层① `novel_groups.group_default_visibility`（NOT NULL）
 */
export function resolveEffectiveVisibility(
  appOverride: NovelVisibility | null,
  novelOverride: NovelVisibility | null,
  groupDefault: NovelVisibility,
): NovelVisibility {
  return appOverride ?? novelOverride ?? groupDefault;
}

/**
 * 列表面展示 / 筛选的合成值（A6 §15.1「受众档的展示口径」）：层② 覆盖层①，**不含层③**。
 *
 * 层③ 是 per-App 的，列表是跨 App 的书组列表、无 App scope 入参，故层③ 只能按 §10 逐 App 查。
 * 列表的 `visibility` query 参数比较值与本函数同源 —— 筛选按合成值、展示也按合成值，
 * 两处若各写各的就会出现「筛出来了但这列是空的」。
 */
export function listDisplayVisibility(
  novelOverride: NovelVisibility | null,
  groupDefault: NovelVisibility,
): NovelVisibility {
  return novelOverride ?? groupDefault;
}
